import { useMemo, useState, type ComponentType, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, Info, SlidersHorizontal } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { ToolbarIconButton } from '@reflex/ui/components/shared/ToolbarIconButton';
import { cn } from '@reflex/ui/lib/utils';
import { usePinnedActionsPref } from '@reflex/ui/client/prefs';
import { SandboxStatusBadge } from '@/components/status/SandboxStatusBadge';
import {
  useAgentActions,
  isAgentHeaderChromeAction,
  AgentActionIconControl,
  AgentActionDialogs,
  type AgentAction,
} from '@/components/agent/agent-actions';
import { HeaderDisplayMenu } from './HeaderDisplayMenu';
import type { HeaderDisplayItem } from './HeaderDisplayMenu';
import { nearestPinnedFallback, pinnedAgentViews } from './useAgentTabLayout';
import type { Agent, AgentActivity } from '@/lib/types';
import type { ResolvedAgentAction, ResolvedAgentTab } from '@/lib/plugin-registry';
import type { SegmentedTabsCustomization } from '@reflex/ui/components/shared/SegmentedTabs';

interface AgentRailToolbarProps {
  agent: Agent;
  activity: AgentActivity;
  hideActions?: boolean;
  agentActions: ResolvedAgentAction[];
  onOpenDetail: () => void;
  onArchived?: () => void;
  availableViews: string[];
  /** Currently selected view tab; used when unpinning the active tab. */
  activeView: string;
  onViewChange: (view: string) => void;
  viewIconMap: Record<string, ComponentType<{ className?: string }>>;
  viewLabelMap?: Record<string, string>;
  tabMap: Map<string, ResolvedAgentTab>;
  customize?: SegmentedTabsCustomization;
  /** Where Display / action menus open. Default `left` for the vertical rail. */
  menuSide?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * `rail` stacks icons vertically (desktop side rail). `toolbar` lays them
   * out as large labeled tiles for the mobile bottom sheet.
   */
  layout?: 'rail' | 'toolbar';
  /**
   * Called when Details opens from the mobile toolbar so the views sheet can
   * close before the details sheet mounts. Dialog actions that dispatch
   * `plugin:command` (Report bug) close the views sheet from that event.
   */
  onDismissSheet?: () => void;
}

const mobileTileClassName = cn(
  'flex min-h-14 w-full flex-col items-center justify-center gap-1.5 rounded-xl',
  'bg-muted/30 px-1.5 py-2.5 text-muted-foreground',
  'transition-colors hover:bg-muted/55 hover:text-foreground active:bg-muted',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  'disabled:pointer-events-none disabled:opacity-50',
);

/**
 * Pinned rail actions stay flat like the tab strip above them — no well, ring,
 * or chip chrome, which reads as a border on the icon when only one action is
 * pinned. The hairline divider below the group is what separates "your
 * actions" from the sandbox / Display / Details chrome.
 */
const railActionGroupClassName = 'flex w-full flex-col items-center gap-0.5';

/** Items are flat, slightly brighter than chrome, hover-lit. */
const railActionClassName = 'text-foreground/70 hover:text-foreground';

function MobileToolTile({
  icon: Icon,
  label,
  onClick,
  disabled,
  className,
  iconClassName,
  'data-testid': testId,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  iconClassName?: string;
  'data-testid'?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      data-testid={testId}
      className={cn(mobileTileClassName, className)}
    >
      <Icon className={cn('size-5 shrink-0', iconClassName)} aria-hidden="true" />
      <span className="max-w-full truncate text-center text-2xs font-medium leading-tight">
        {label}
      </span>
    </button>
  );
}

function MobilePinnedActionTile({
  action,
  onOpenDialog,
}: {
  action: AgentAction;
  onOpenDialog: (key: string) => void;
}) {
  if (action.kind === 'submenu') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={action.pending}
            aria-label={action.label}
            data-testid={`agent-action-${action.key}`}
            className={mobileTileClassName}
          >
            <action.icon
              className={cn('size-5 shrink-0', action.iconClassName)}
              aria-hidden="true"
            />
            <span className="max-w-full truncate text-center text-2xs font-medium leading-tight">
              {action.label}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="center">
          {action.items.map((item) => (
            <DropdownMenuItem key={item.key} onSelect={item.onSelect}>
              <item.icon className={cn('mr-2 h-4 w-4', item.iconClassName)} />
              {item.label}
              {item.checked && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (action.kind === 'run') {
    return (
      <MobileToolTile
        icon={action.icon}
        label={action.label}
        disabled={action.pending}
        onClick={action.onSelect}
        iconClassName={action.iconClassName}
        className={action.destructive ? 'text-destructive' : undefined}
        data-testid={`agent-action-${action.key}`}
      />
    );
  }

  if (action.kind === 'dialog') {
    return (
      <MobileToolTile
        icon={action.icon}
        label={action.label}
        disabled={action.pending}
        onClick={() => onOpenDialog(action.key)}
        iconClassName={action.iconClassName}
        className={action.destructive ? 'text-destructive' : undefined}
        data-testid={`agent-action-${action.key}`}
      />
    );
  }

  if (action.kind === 'link') {
    return (
      <Button variant="ghost" asChild className={cn(mobileTileClassName, 'h-auto')}>
        <Link to={action.to} aria-label={action.label} data-testid={`agent-action-${action.key}`}>
          <action.icon className="size-5 shrink-0" aria-hidden="true" />
          <span className="max-w-full truncate text-center text-2xs font-medium leading-tight">
            {action.label}
          </span>
        </Link>
      </Button>
    );
  }

  return null;
}

function SortablePinnedAction({
  action,
  onOpenDialog,
  tooltipSide = 'left',
}: {
  action: AgentAction;
  onOpenDialog: (key: string) => void;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}) {
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: action.key,
  });
  return (
    <div
      ref={setNodeRef}
      data-slot="rail-action-sortable"
      data-testid={`agent-rail-sortable-${action.key}`}
      className={cn(isDragging && 'z-10 opacity-80')}
      style={{
        transform: CSS.Translate.toString(transform && { ...transform, x: 0 }),
        transition,
      }}
      {...listeners}
    >
      <AgentActionIconControl
        action={action}
        onOpenDialog={onOpenDialog}
        side={tooltipSide}
        className={railActionClassName}
      />
    </div>
  );
}

/**
 * Bottom of the agent right rail: pinned action icons, then sandbox /
 * display / details. Pinning lives in the Display menu; header chrome
 * (Mark as / Share / Launch again / …) never appears here.
 */
export function AgentRailToolbar({
  agent,
  activity,
  hideActions,
  agentActions,
  onOpenDetail,
  onArchived,
  availableViews,
  activeView,
  onViewChange,
  viewIconMap,
  viewLabelMap = {},
  tabMap,
  customize,
  menuSide = 'left',
  layout = 'rail',
  onDismissSheet,
}: AgentRailToolbarProps) {
  const actions = useAgentActions(agent, { onArchived, agentActions, activity });
  const [openDialogKey, setOpenDialogKey] = useState<string | null>(null);
  const [pinnedKeys, setPinnedKeys] = usePinnedActionsPref();
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );
  const tooltipSide = menuSide === 'left' ? 'left' : 'top';
  const isToolbar = layout === 'toolbar';

  const pinnedSet = useMemo(() => new Set(pinnedKeys), [pinnedKeys]);
  const actionByKey = useMemo(
    () => new Map(actions.map((action) => [action.key, action])),
    [actions],
  );

  const customizableActions = useMemo(
    () => actions.filter((action) => !isAgentHeaderChromeAction(action)),
    [actions],
  );
  // Prefer the viewer's pin order; drop header-chrome keys left over from older
  // defaults and anything that is not currently available on this agent.
  const pinnedActions = useMemo(
    () =>
      pinnedKeys
        .map((key) => actionByKey.get(key))
        .filter(
          (action): action is AgentAction => action != null && !isAgentHeaderChromeAction(action),
        ),
    [pinnedKeys, actionByKey],
  );
  const pinnedActionKeys = useMemo(
    () => pinnedActions.map((action) => action.key),
    [pinnedActions],
  );

  const tabDisplayItems = useMemo<HeaderDisplayItem[]>(
    () =>
      availableViews.map((v) => ({
        key: v,
        label: viewLabelMap[v] ?? tabMap.get(v)?.label ?? v,
        icon: viewIconMap[v],
      })),
    [availableViews, viewLabelMap, tabMap, viewIconMap],
  );

  const actionDisplayItems = useMemo(
    () =>
      customizableActions.map((action) => ({
        key: action.key,
        label: action.label,
        icon: action.icon,
        iconClassName: action.iconClassName,
      })),
    [customizableActions],
  );

  const showActions = !hideActions;

  const handlePinnedDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = pinnedActionKeys.indexOf(String(active.id));
    const to = pinnedActionKeys.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const nextVisible = arrayMove(pinnedActionKeys, from, to);
    // Project the visible reorder onto the full pin list so header-chrome
    // leftovers (and keys for unavailable actions) keep their absolute slots.
    const slots = pinnedKeys.flatMap((key, i) => (pinnedActionKeys.includes(key) ? [i] : []));
    const next = [...pinnedKeys];
    slots.forEach((slot, i) => {
      next[slot] = nextVisible[i];
    });
    setPinnedKeys(next);
  };

  const displayTrigger: ReactElement | undefined = isToolbar ? (
    <button
      type="button"
      className={mobileTileClassName}
      aria-label="Display"
      data-testid="header-display-menu"
    >
      <SlidersHorizontal className="size-5 shrink-0" aria-hidden="true" />
      <span className="max-w-full truncate text-center text-2xs font-medium leading-tight">
        Display
      </span>
    </button>
  ) : undefined;

  const displayMenu = (
    <HeaderDisplayMenu
      menuSide={menuSide}
      layout={isToolbar ? 'flat' : 'nested'}
      tabItems={tabDisplayItems}
      tabHiddenSet={customize ? new Set(customize.hidden) : new Set()}
      onToggleTab={(key) => {
        if (!customize) return;
        const isHidden = customize.hidden.includes(key);
        if (isHidden) {
          customize.onHiddenChange(customize.hidden.filter((h) => h !== key));
          return;
        }
        // Refuse to unpin the last remaining rail tab.
        if (pinnedAgentViews(availableViews, customize.hidden).length <= 1) return;
        if (key === activeView) {
          const fallback = nearestPinnedFallback(availableViews, customize.hidden, key);
          if (fallback) onViewChange(fallback);
        }
        customize.onHiddenChange([...customize.hidden, key]);
      }}
      onResetTabs={customize?.onReset}
      actionItems={showActions ? actionDisplayItems : []}
      actionPinnedSet={pinnedSet}
      onToggleActionPinned={(key) => {
        const action = actionByKey.get(key);
        if (!action || isAgentHeaderChromeAction(action)) return;
        const next = pinnedSet.has(key)
          ? pinnedKeys.filter((k) => k !== key)
          : [...pinnedKeys, key];
        setPinnedKeys(next);
      }}
      trigger={displayTrigger}
    />
  );

  const dialogs = showActions ? (
    <AgentActionDialogs
      actions={actions}
      agent={agent}
      openKey={openDialogKey}
      onOpenKeyChange={setOpenDialogKey}
    />
  ) : null;

  if (isToolbar) {
    return (
      <>
        <div className="grid w-full grid-cols-4 gap-2" data-testid="agent-rail-toolbar-grid">
          {showActions
            ? pinnedActions.map((action) => (
                <MobilePinnedActionTile
                  key={action.key}
                  action={action}
                  onOpenDialog={(key) => setOpenDialogKey(key)}
                />
              ))
            : null}
          <div
            className={cn(mobileTileClassName, 'relative cursor-pointer overflow-hidden p-0')}
            data-testid="mobile-tool-sandbox"
          >
            <SandboxStatusBadge
              agentId={agent.id}
              devboxId={agent.devboxId}
              agentStatus={agent.status}
              resourceSize={agent.sandboxOptions?.resourceSize}
              iconOnly
              className="!h-full !min-h-14 !w-full !flex-col !justify-start !gap-0 !rounded-xl !pt-2.5 !pb-6 [&_svg]:!size-5"
            />
            <span className="pointer-events-none absolute inset-x-1 bottom-2.5 truncate text-center text-2xs font-medium leading-tight text-muted-foreground">
              Sandbox
            </span>
          </div>
          {displayMenu}
          <MobileToolTile
            icon={Info}
            label="Details"
            onClick={() => {
              onDismissSheet?.();
              onOpenDetail();
            }}
          />
        </div>
        {dialogs}
      </>
    );
  }

  return (
    <>
      {showActions && pinnedActions.length > 0 ? (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handlePinnedDragEnd}
          >
            <SortableContext items={pinnedActionKeys} strategy={verticalListSortingStrategy}>
              <div
                role="group"
                aria-label="Pinned actions"
                className={railActionGroupClassName}
                data-testid="agent-rail-pinned"
              >
                {pinnedActions.map((action) => (
                  <SortablePinnedAction
                    key={action.key}
                    action={action}
                    tooltipSide={tooltipSide}
                    onOpenDialog={(key) => setOpenDialogKey(key)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <div
            className="my-1.5 h-px w-5 bg-border"
            aria-hidden="true"
            data-testid="agent-rail-divider"
          />
        </>
      ) : null}

      <SandboxStatusBadge
        agentId={agent.id}
        devboxId={agent.devboxId}
        agentStatus={agent.status}
        resourceSize={agent.sandboxOptions?.resourceSize}
        iconOnly
      />
      {displayMenu}
      <ToolbarIconButton
        icon={Info}
        label="Details"
        tooltipSide={tooltipSide}
        onClick={onOpenDetail}
      />
      {dialogs}
    </>
  );
}
